"use client"

import { useId, useState } from "react"

/** Hide what does not match `q` inside the scope; returns how many items stay visible (null for an empty query). */
function applyFilter(scopeId: string, q: string): number | null {
  const scope = document.getElementById(scopeId)
  if (!scope) return null
  const needle = q.trim().toLowerCase()
  let visible = 0
  for (const group of Array.from(scope.querySelectorAll<HTMLElement>("[data-dir-group]"))) {
    const groupMatch = needle !== "" && (group.dataset.dirGroup ?? "").includes(needle)
    let any = false
    for (const item of Array.from(group.querySelectorAll<HTMLElement>("li"))) {
      const hit = needle === "" || groupMatch || (item.textContent ?? "").toLowerCase().includes(needle)
      item.hidden = !hit
      if (hit) { any = true; visible++ }
    }
    group.hidden = !any
  }
  return needle === "" ? null : visible
}
/**
 * FRIDGE-1280 — "find a collection" box for /collections/all.
 *
 * Progressive enhancement only: every link stays in the server-rendered HTML
 * (the directory's crawl contract — each live collection linked once), and
 * this island merely hides what does not match. Each group (a brand card or
 * a heading's list) carries `data-dir-group`; its items are its `li` elements,
 * matched on their own link text, so no extra attribute rides on ~700 links.
 * A group hides when none of its items match, or shows whole when its own name
 * matches.
 */
export function DirectoryFilter({ scopeId, total }: { scopeId: string; total: number }) {
  const [q, setQ] = useState("")
  const [shown, setShown] = useState<number | null>(null)
  const id = useId()

  return (
    <div className="mt-8 max-w-xl">
      <label htmlFor={id} className="block text-xs font-bold uppercase tracking-[0.12em] text-gray-500">
        Find a collection on this page
      <input
        id={id}
        aria-label="Find a collection on this page"
        type="search"
        value={q}
        onChange={(e) => { setQ(e.target.value); setShown(applyFilter(scopeId, e.target.value)) }}
        placeholder="Try “counter depth”, “LG”, “36 inch”…"
        autoComplete="off"
        className="mt-2 block w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-base font-normal normal-case tracking-normal text-gray-900 placeholder:text-gray-500 focus:border-[#0469f0] focus:outline-none focus:ring-2 focus:ring-[#0469f0]/20"
      />
      </label>
      <p role="status" aria-live="polite" className="mt-2 text-sm text-gray-600 min-h-[1.25rem]">
        {shown === null ? "" : shown === 0 ? "No collection on this page matches. Try another word, or the brand directory." : `${shown.toLocaleString("en-US")} of ${total.toLocaleString("en-US")} collections on this page match.`}
      </p>
    </div>
  )
}
